import { useState, useRef, useEffect, useCallback } from "react"
import { askSupport, IFaq, AskMode } from "@/shared/api/support"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from 'yup'
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import XSvg from '@/shared/assets/images/X.svg'
import * as styles from './support-bot.module.scss'
import { animated, useTransition, useSpring } from "@react-spring/web"
import useOutsideAlerter from "@/shared/lib/use-outside-alerter"

const schema = yup.object().shape({
    question: yup
        .string()
        .required("Введите вопрос")
        .min(1, "Вопрос не может быть пустым")
}) as yup.ObjectSchema<SupportForm>

type SupportForm = {
    question: string
}

interface Message {
    id: string
    type: 'user' | 'assistant'
    content: string
    mode?: AskMode
    faqList?: IFaq[]
}

const MessageItem = ({ message, index, setQuery }: { message: Message; index: number; setQuery: (query: string) => void }) => {
    const messageAnimation = useSpring({
        from: {
            opacity: 0,
            transform: message.type === 'user' ? 'translateX(20px)' : 'translateX(-20px)',
        },
        to: {
            opacity: 1,
            transform: 'translateX(0px)',
        },
        delay: index * 50,
        config: { tension: 300, friction: 25 }
    })

    const renderContent = () => {
        if (message.type === 'user') {
            return (
                <div
                    className={`max-w-[80%] rounded-3xl p-3 bg-color-primary text-white`}
                >
                    <div className="whitespace-pre-wrap break-words text-sm">
                        {message.content}
                    </div>
                </div>
            )
        }

        // Handling modes for assistant responses
        const mode = message.mode || 'accept'
        
        switch (mode) {
            case 'accept':
                // Mode accept: show answer as is, without additional elements
                return (
                    <div
                        className={`max-w-[80%] rounded-3xl p-3 bg-white text-color-dark border border-[#EAECF0]`}
                    >
                        <div className="whitespace-pre-wrap break-words text-sm">
                            {message.content}
                        </div>
                    </div>
                )

            case 'merge':
                // Mode merge: show merged answer + list of related FAQ from qa_list
                return (
                    <div className="flex flex-col gap-[4px] max-w-[80%]">
                        <div
                            className={`w-full rounded-3xl p-3 bg-white text-color-dark border border-[#EAECF0]`}
                        >
                            <div className="whitespace-pre-wrap break-words text-sm">
                                Возможно, вы имели в виду:
                            </div>
                        </div>
                        {message.faqList && message.faqList.length > 0 && (
                            <div className="flex flex-col gap-[4px]">
                                {message.faqList.map((faq) => (
                                    <div
                                        key={faq.id}
                                        className="text-[11px] w-fit text-center text-white p-1 bg-color-primary rounded-full cursor-pointer transition-colors"
                                        onClick={() => {
                                            setQuery(faq.question)
                                        }}
                                    >
                                        {faq.question}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )

            case 'clarify':
                // Mode clarify: show message with suggestion to rephrase + similar questions
                return (
                    <div className="flex flex-col gap-[4px] max-w-[80%]">
                        <div
                            className={`w-full rounded-3xl p-3 bg-white text-color-dark border border-[#EAECF0]`}
                        >
                            <div className="whitespace-pre-wrap break-words text-sm">
                                Возможно, вы имели в виду:
                            </div>
                        </div>
                        {message.faqList && message.faqList.length > 0 && (
                            <div className="flex flex-col gap-[4px]">
                                {message.faqList.map((faq) => (
                                    <div
                                        key={faq.id}
                                        className="text-[11px] w-fit text-center text-white p-1 bg-color-primary rounded-full cursor-pointer transition-colors"
                                        onClick={() => {
                                            setQuery(faq.question)
                                        }}
                                    >
                                        {faq.question}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )

            case 'super_clarify':
                return (
                    <div
                        className={`max-w-[80%] rounded-3xl p-3 bg-white text-color-dark border border-[#EAECF0]`}
                    >
                        <div className="whitespace-pre-wrap break-words text-sm">
                            {message.content}
                        </div>
                    </div>
                )

            default:
                return (
                    <div
                        className={`max-w-[80%] rounded-3xl p-3 bg-white text-color-dark border border-[#EAECF0]`}
                    >
                        <div className="whitespace-pre-wrap break-words text-sm">
                            {message.content}
                        </div>
                    </div>
                )
        }
    }

    return (
        <animated.div
            style={messageAnimation}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
        >
            {renderContent()}
        </animated.div>
    )
}

export const SupportBot = () => {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [messages, setMessages] = useState<Message[]>([])
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    
    const dropdownTransition = useTransition(isOpen, {
        from: {
            opacity: 0,
            transform: 'translateY(20px) scale(0.95)',
        },
        enter: {
            opacity: 1,
            transform: 'translateY(0px) scale(1)',
        },
        leave: {
            opacity: 0,
            transform: 'translateY(20px) scale(0.95)',
        },
        config: { tension: 300, friction: 25 }
    })

    const buttonRotation = useSpring({
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        config: { tension: 300, friction: 20 }
    })

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { isSubmitting },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            question: ''
        }
    })

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }

    useEffect(() => {
        if (isOpen) {
            scrollToBottom()
        }
    }, [messages, isOpen])

    const handleClickOutside = useCallback(() => {
        if (isOpen) {
            setIsOpen(false)
        }
    }, [isOpen])

    useOutsideAlerter(containerRef, handleClickOutside)

    const onSubmit = async (data: SupportForm) => {
        setIsLoading(true)
        
        const userMessage: Message = {
            id: Date.now().toString(),
            type: 'user',
            content: data.question
        }
        setMessages(prev => [...prev, userMessage])

        try {
            const response = await askSupport({ question: data.question })
            const askData = response.data

            const assistantMessage: Message = {
                id: (Date.now() + 1).toString(),
                type: 'assistant',
                content: askData.answer,
                mode: askData.mode,
                faqList: askData.qa_list
            }
            setMessages(prev => [...prev, assistantMessage])
            reset()
        } catch (error: any) {
            const errorId = error?.response?.data?.error_id
            const errorMessage = getErrorMessage(errorId) || "Ошибка при отправке вопроса"
            notify.error(errorMessage)
            
            setMessages(prev => prev.filter(msg => msg.id !== userMessage.id))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div ref={containerRef} className="fixed bottom-6 right-6 z-50">
            {dropdownTransition((style, item) => 
                item && (
                    <animated.div
                        ref={dropdownRef}
                        style={style}
                        className="absolute bottom-16 right-0 w-[400px] h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col border border-[#EAECF0]"
                    >
                        <div className="flex items-center justify-between p-4 border-b border-[#EAECF0] bg-color-primary rounded-t-2xl">
                            <div className="text-white font-semibold text-lg">Поддержка OEM</div>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
                            >
                                <XSvg width={20} height={20} className="fill-white" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#F9F9F9]">
                            {messages.length === 0 ? (
                                <div className="flex items-center justify-center h-full text-color-dark/70">
                                    <div className="text-center">
                                        <div className="text-lg font-semibold mb-2">Добро пожаловать в поддержку</div>
                                        <div className="text-sm">Задайте ваш вопрос, и мы поможем вам</div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {messages.map((message, index) => (
                                        <MessageItem key={message.id} message={message} index={index} setQuery={(query: string) => setValue('question', query)} />
                                    ))}
                                    <div ref={messagesEndRef} />
                                </>
                            )}
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)} className="border-t border-[#EAECF0] p-4 bg-white rounded-b-2xl">
                            <div className="flex gap-2">
                                <input
                                    {...register('question')}
                                    placeholder="Введите ваш вопрос..."
                                    className="flex-1 px-4 py-2 border border-[#EAECF0] rounded-full outline-none focus:border-color-primary text-ellipsis overflow-hidden text-sm"
                                    disabled={isLoading}
                                />
                                <button
                                    type="submit"
                                    disabled={isLoading || isSubmitting}
                                    className="px-6 py-2 bg-color-primary text-white rounded-full hover:bg-color-primary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold whitespace-nowrap"
                                >
                                    {isLoading ? '...' : 'Отправить'}
                                </button>
                            </div>
                        </form>
                    </animated.div>
                )
            )}

            <animated.button
                ref={buttonRef}
                onClick={(e) => {
                    e.stopPropagation()
                    setIsOpen(prev => !prev)
                }}
                style={buttonRotation}
                className="w-14 h-14 bg-color-primary rounded-full shadow-lg hover:bg-color-primary/80 transition-colors flex items-center justify-center text-white text-2xl font-bold relative"
                aria-label="Открыть поддержку"
            >
                {isOpen ? (
                    <XSvg width={24} height={24} className="fill-white" />
                ) : (
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H6L4 18V4H20V16Z"
                            fill="white"
                        />
                    </svg>
                )}
                <div className={styles.buttonPulse} />
            </animated.button>
        </div>
    )
}
